import {Fragment, useMemo, useState} from 'react'
import {StyleSheet, Text, TextInput, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {blankInputWidth} from '#/lib/blankInputWidth'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {useTextWidths} from '#/components/capsules/play/useTextWidths'
import {
  useFlashBookPlayPalette,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayPrimaryAction,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
  usePlayTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {SLIDE_TYPE_META} from '#/types/capsule'

/**
 * The blank field's own type, in one place.
 *
 * Its width is *measured* in this font and grown by this padding, so the numbers
 * the field is drawn with and the numbers its width is worked out from have to be
 * the same numbers — spelled twice, they drift, and a drifted padding is a field
 * whose text does not quite fit the box it was sized for.
 */
const BLANK_TYPE = {
  fontSize: 13,
  fontWeight: '600',
  paddingHorizontal: 8,
  borderWidth: 1.5,
} as const

/** What the field's width has to cover beyond the writing: padding and borders. */
const BLANK_PADDING =
  (BLANK_TYPE.paddingHorizontal + BLANK_TYPE.borderWidth) * 2

export function FillBlankSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  feedbackState,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const p = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The sentence is text printed on the page, so it is set in the book's own
  // reading type and its line box is one line of the page's ruling.
  const bodyText = usePlayTextStyle('body')
  const lineBox = bodyText.lineHeight ?? 22
  // A whole number of the page's rules between the question and the exercise.
  const stackGap = usePlayStackGap(16)
  // Inside a book the block owns the surface: an open/ruled page writes the
  // sentence straight on the paper, a panel theme frames it with the block.
  const onPaper = usePlaySuppressOwnSurface()
  // The action strip is page furniture: on ruled paper it is the button alone,
  // printed on the lines, a whole number of rules tall.
  const footerStyle = usePlayFooterStyle()
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  const blanks = slide.blanks ?? []
  const [inputs, setInputs] = useState<string[]>(Array(blanks.length).fill(''))
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  /*
   * The width of a blank is the width of the words it can hold, in the font the
   * field prints them in — which is this input's own, from the second weight
   * down, and not the sentence's around it.
   */
  const blankFont = useMemo(
    () => ({
      fontFamily: bodyText.fontFamily,
      fontSize: BLANK_TYPE.fontSize,
      fontWeight: BLANK_TYPE.fontWeight,
    }),
    [bodyText.fontFamily],
  )
  const {widths, probe} = useTextWidths(blankFont, [
    ...blanks.flatMap(blank => [blank.answer, ...(blank.alternatives ?? [])]),
    ...inputs,
  ])

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setInputs(Array(blanks.length).fill(''))
  }

  const isSolution = feedbackState === 'solution'

  const isBlankCorrect = (i: number) => {
    const blank = blanks[i]
    if (!blank) return false
    const raw = (inputs[i] ?? '').trim().toLowerCase()
    const answers = [blank.answer, ...(blank.alternatives ?? [])].map(a =>
      a.trim().toLowerCase(),
    )
    return answers.includes(raw)
  }

  const handleCheck = () => {
    const maxPoints = blanks.length
    const points = blanks.filter((_, i) => isBlankCorrect(i)).length
    onAnswer({
      isCorrect: points === maxPoints,
      points,
      maxPoints,
      solutionText: blanks.map(b => b.answer).join(' · '),
      explanation: slide.explanation,
      userAnswer: inputs,
    })
  }

  const allFilled = inputs.every(v => v.trim().length > 0)

  // Build inline segments: [before_0, slot_0, middle_0_1, slot_1, ..., after_last]
  // Each blank stores: before (text before this blank), answer, after (text up to next blank)
  // To avoid duplicate rendering, use blank[0].before then for each blank: slot + blank[i].after
  const segments: Array<{text: string} | {blankIdx: number}> = []
  if (blanks.length > 0) {
    if (blanks[0].before) segments.push({text: blanks[0].before})
    blanks.forEach((blank, i) => {
      segments.push({blankIdx: i})
      if (blank.after) segments.push({text: blank.after})
    })
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}
        keyboardShouldPersistTaps="handled">
        {/*
          The probe each blank's width is measured with, on the page rather than
          in the sentence: inside the wrapping row it would be measured against a
          width the blanks themselves decide.
        */}
        {probe}
        {!!slide.question && (
          <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
            {slide.question}
          </QuestionHeading>
        )}

        <View
          style={[
            ss.paragraphCard,
            onPaper
              ? {
                  backgroundColor: 'transparent',
                  // The frame goes, not just its colour: transparent border
                  // pixels are still height the ruling does not have.
                  borderWidth: 0,
                  padding: 0,
                }
              : {backgroundColor: p.surfaceAlt, borderColor: p.border},
          ]}>
          <View style={[ss.inlineRow, onPaper && {rowGap: 0}]}>
            {segments.map((seg, si) => {
              if ('text' in seg) {
                return seg.text
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word, wi) => (
                    <Text
                      key={`${si}-${wi}`}
                      style={[ss.word, bodyText, {color: p.body}]}>
                      {word}
                    </Text>
                  ))
              }

              const i = seg.blankIdx
              const blank = blanks[i]
              const correct =
                showFeedback && !isSolution ? isBlankCorrect(i) : null
              const displayValue = isSolution ? blank.answer : (inputs[i] ?? '')
              const borderColor = isSolution
                ? p.warning
                : showFeedback
                  ? isBlankCorrect(i)
                    ? p.positive
                    : p.negative
                  : primaryColor + '80'
              // The blank is as wide as the word it is waiting for (including
              // any accepted alternative), and grows with what the learner
              // actually types so a longer attempt is never clipped. The width
              // is what the platform measured those words at, in this field's own
              // font — not a character count times an average.
              const inputWidth = blankInputWidth(
                [blank.answer, ...(blank.alternatives ?? []), inputs[i]],
                {font: blankFont, padding: BLANK_PADDING, measured: widths},
              )

              return (
                <Fragment key={`blank-${i}`}>
                  <View style={ss.inputWrap}>
                    <TextInput
                      style={[
                        ss.input,
                        {
                          width: inputWidth,
                          borderColor,
                          fontFamily: bodyText.fontFamily,
                        },
                        // The field is one line of the page: taller would push
                        // the writing off the rule it sits on.
                        onPaper && {height: lineBox},
                        // On paper a blank is a ruled space to write on; a
                        // boxed theme keeps the filled form field.
                        onPaper
                          ? {
                              borderRadius: 0,
                              borderWidth: 0,
                              borderBottomWidth: 2,
                              backgroundColor: 'transparent',
                              color: isSolution ? p.warning : p.text,
                            }
                          : {
                              color: isSolution ? p.warning : p.inputText,
                              backgroundColor: isSolution
                                ? p.warning + '11'
                                : p.inputBg,
                            },
                      ]}
                      value={displayValue}
                      onChangeText={val =>
                        setInputs(prev => {
                          const next = [...prev]
                          next[i] = val
                          return next
                        })
                      }
                      editable={!showFeedback}
                      autoCapitalize="none"
                      autoCorrect={false}
                      placeholder="…"
                      placeholderTextColor={p.muted}
                      accessibilityLabel={l`Fill in the blank`}
                      accessibilityHint={undefined}
                      textAlign="center"
                    />
                    {showFeedback && !isSolution && correct !== null && (
                      <Feather
                        name={correct ? 'check-circle' : 'x-circle'}
                        size={14}
                        color={correct ? p.positive : p.negative}
                        style={ss.feedbackIcon}
                      />
                    )}
                  </View>
                </Fragment>
              )
            })}
          </View>
        </View>
      </PlayScroller>

      {!showFeedback && (
        <View style={[ss.footer, {borderTopColor: p.border}, footerStyle]}>
          <TouchableOpacity
            style={[
              ss.checkBtn,
              {backgroundColor: allFilled ? primaryColor : p.surfaceAlt},
              action.style,
            ]}
            onPress={handleCheck}
            disabled={!allFilled}
            accessibilityRole="button"
            accessibilityLabel={l`Check answer`}
            accessibilityHint={undefined}>
            <Feather
              name="check"
              size={16}
              color={
                action.ink(allFilled) ?? (allFilled ? p.onAccent : p.muted)
              }
            />
            <Text
              style={[
                ss.checkBtnText,
                {
                  color:
                    action.ink(allFilled) ?? (allFilled ? p.onAccent : p.muted),
                },
                action.labelStyle(allFilled),
              ]}>
              <Trans>Check</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16, paddingBottom: 32},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  paragraphCard: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    padding: 16,
  },
  inlineRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    rowGap: 8,
  },
  word: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 22,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  input: {
    height: 32,
    paddingHorizontal: BLANK_TYPE.paddingHorizontal,
    borderRadius: 7,
    borderWidth: BLANK_TYPE.borderWidth,
    fontSize: BLANK_TYPE.fontSize,
    fontWeight: BLANK_TYPE.fontWeight,
  },
  feedbackIcon: {
    marginLeft: 2,
  },
  explanationBox: {
    padding: 14,
    backgroundColor: PLAY_SURFACE.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
  },
  explanationText: {
    fontSize: 14,
    color: PLAY_SURFACE.textMuted,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  checkBtn: {
    flexDirection: 'row',
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
